// Shared error vocabulary for the app builder error UI (composer banner, page
// header, and in-thread transcript markers). Call sites normalize messy
// failures into AppBuilderErrorIdentity; classifyAppBuilderError maps that
// identity to one of five product buckets:
//
// 1 retryable_now — transient turn/stream failure; yellow banner + Try again
// 2a provider_outage — LLM provider down; yellow + Try again + View status
// 2c infra_outage — GitHub/Neon/Vercel down; blocking callout above composer
// 2b try_later — turn conflict / capacity; yellow + Try again
// 3 credits — out of build credits; blocking callout above composer
// 4 terminal — unrecoverable build failure; red + Start over
// 5 outside-builder — pre-turn / connection failures; red page header only
//
// Buckets 1, 2a, 2b and 4 render in the composer via AppBuilderErrorBanner.
// Buckets 2c and 3 are classified here but rendered by their own callouts
// (InfraProviderOutageBlock, OutOfBuildCreditsBanner) so each can carry i18n
// copy and its own CTA; bucket 5 is routed to BuilderSessionHeaderError and
// must not duplicate in the composer.
// In-thread `{ type: 'error' }` parts use getAppBuilderTranscriptError for
// quieter, bucket-aware copy in the chat scroll area.
export type AppBuilderErrorCategory =
  | 'retryable_now'
  | 'retryable_later'
  | 'terminal';

// Sub-buckets within retryable_later — each drives different copy and which
// recovery actions are offered. budget_exceeded keeps Try again so the user can
// retry a smaller request; credits leaves the error banner entirely for
// OutOfBuildCreditsBanner, whose Upgrade CTA is the only useful action.
export type AppBuilderErrorLaterReason =
  | 'try_later'
  | 'credits'
  | 'budget_exceeded'
  | 'provider_outage'
  | 'infra_outage';

export interface AppBuilderErrorIdentity {
  code?: string;
  message?: string;
  status?: number;
  // Where the failure originated — deploy text is only terminal when source is
  // `builder`, since the same substring can appear on transient stream errors.
  source?: 'request' | 'stream' | 'message' | 'builder' | 'connection';
}

// Hooks may still hold legacy string errors until every path is structured.
export type AppBuilderErrorState = AppBuilderErrorIdentity | string;

type MessageWithErrorParts = {
  parts?: Array<{
    type?: string;
    reasonCode?: string;
    errorMessage?: string;
  }>;
};

// Only the latest message's terminal error part is actionable — a follow-up user
// message supersedes an older in-thread failure for routing and banner priority.
export const getLatestInThreadErrorIdentity = (
  messages: MessageWithErrorParts[],
): AppBuilderErrorIdentity | null => {
  const lastMessage = messages[messages.length - 1];
  const terminalPart = lastMessage?.parts?.at(-1);
  if (
    terminalPart?.type !== 'error' ||
    terminalPart.reasonCode === 'user_stopped'
  ) {
    return null;
  }
  return {
    code: terminalPart.reasonCode,
    message: terminalPart.errorMessage,
    source: 'message',
  };
};

export const hasActionableInThreadError = (
  messages: MessageWithErrorParts[],
): boolean => getLatestInThreadErrorIdentity(messages) !== null;

export type BlockingInfraProvider = 'github' | 'neon' | 'vercel';

export const CLAUDE_STATUS_PAGE_URL = 'https://status.claude.com/';

export interface ClassifiedAppBuilderError {
  category: AppBuilderErrorCategory;
  laterReason?: AppBuilderErrorLaterReason;
  /** Set when GitHub, Neon, or Vercel is down — drives InfraProviderOutageBlock. */
  infraProvider?: BlockingInfraProvider;
  message: string;
  variant: 'error' | 'warning' | 'info';
  offerRetry?: boolean;
}

const DEFAULT_ERROR_MESSAGE = 'Something went wrong building your app.';
const TRY_LATER_ERROR_MESSAGE =
  'The app builder is busy right now — try again in a few minutes.';
const PROVIDER_OUTAGE_ERROR_MESSAGE =
  'Our LLM provider was down for this request';
// Classified copy for infra_outage — InfraProviderOutageBlock renders i18n instead.
const BLOCKING_INFRA_OUTAGE_CLASSIFIED_MESSAGE =
  'An infrastructure provider is temporarily unavailable';
// Classified copy for credits — OutOfBuildCreditsBanner renders i18n instead.
const OUT_OF_CREDITS_CLASSIFIED_MESSAGE =
  'The workspace is out of build credits';
// Turn aborted mid-flight at Max_credits_turn — partial work is preserved and
// the workspace is still charged for the cap. Distinct from balance ≤ 0.
const BUDGET_EXCEEDED_BANNER_MESSAGE =
  'This turn hit the per-turn credit limit. Your partial work was saved — try a smaller request, or upgrade for more credits.';
const TERMINAL_ERROR_MESSAGE = "We couldn't build this app. Please start over.";
// A send that never reached the backend (rejected, or sent then navigated-away
// and never persisted). Retryable now — Try again resends the same message.
export const SEND_FAILED_BANNER_MESSAGE = "Your message wasn't sent.";
// Bucket 5 — failures outside the turn/messaging flow (pre-turn send, SSE setup).
export const OUTSIDE_BUILDER_ERROR_MESSAGE =
  'Something went wrong. Try sending a new message or building a new app.';

export const APP_BUILDER_TRANSCRIPT_ERROR_DEFAULT = 'Something went wrong.';
export const APP_BUILDER_TRANSCRIPT_ERROR_CREDITS =
  'This message failed because the workspace was out of build credits.';
export const APP_BUILDER_TRANSCRIPT_ERROR_BUDGET_EXCEEDED =
  'This turn hit the per-turn credit limit. Partial work was saved.';
export const APP_BUILDER_TRANSCRIPT_ERROR_TERMINAL =
  "We couldn't build this app.";

export const BUDGET_EXCEEDED_REASON_CODE = 'budget_exceeded';

const TERMINAL_CODES = new Set([
  'build_not_retryable',
  'deploy_failed',
  'not_found',
  'plan_parse_failed',
  'product_agent_not_configured',
  'repo_token_failed',
  'unauthorized_action',
]);

const TRANSIENT_STATUSES = new Set([408, 429, 500, 502, 503, 504]);
const TERMINAL_STATUSES = new Set([401, 403, 404]);

const BUILDER_DEPLOY_FAILURE_MARKERS = [
  'auto-deploy dispatch failed',
  'deploy failed',
  'deployment failed',
];

export const DEPLOYMENT_STATUS_UNKNOWN_MARKER =
  'vercel: deployment status unknown';

const DEPLOYMENT_STATUS_UNKNOWN_MESSAGE =
  'We could not confirm whether publishing finished. Try publishing again.';

export const isDeploymentStatusUnknownError = (message: string): boolean =>
  message.toLowerCase().includes(DEPLOYMENT_STATUS_UNKNOWN_MARKER);

const PROVIDER_OUTAGE_MARKERS = [
  'overloaded',
  'at capacity',
  'anthropic',
  'too busy',
];

const isProviderOutageError = (message: string, status?: number): boolean =>
  status === 529 || includesOneOf(message, PROVIDER_OUTAGE_MARKERS);

const INFRA_OUTAGE_HTTP_STATUSES = new Set([502, 503, 504]);

const INFRA_OUTAGE_MESSAGE_MARKERS = [
  'no server is currently available',
  'service unavailable',
  'bad gateway',
  'temporarily unavailable',
  'gateway timeout',
  'failed with status 502',
  'failed with status 503',
  'failed with status 504',
  'failed with status 500',
];

const GENERIC_DISPATCH_SETUP_ERROR = 'something went wrong while setting up';

const isBlockingInfraOutageSignal = (
  message: string,
  status?: number,
): boolean =>
  (status !== undefined && INFRA_OUTAGE_HTTP_STATUSES.has(status)) ||
  includesOneOf(message, INFRA_OUTAGE_MESSAGE_MARKERS) ||
  /failed with status 5\d\d/.test(message);

const isAuthOrConfigFailure = (message: string, status?: number): boolean =>
  (status !== undefined && TERMINAL_STATUSES.has(status)) ||
  includesOneOf(message, [
    'unauthorized',
    'not configured',
    'http 401',
    'http 403',
    'http 404',
  ]);

/** Detects whether a failure indicates GitHub, Neon, or Vercel is down. */
export const detectBlockingInfraProviderOutage = (
  identity: AppBuilderErrorIdentity,
): BlockingInfraProvider | null => {
  const message = identity.message?.toLowerCase() ?? '';
  const { status } = identity;
  if (!message && status === undefined) return null;
  if (isAuthOrConfigFailure(message, status)) return null;
  if (!isBlockingInfraOutageSignal(message, status)) return null;

  if (
    includesOneOf(message, [
      'github',
      'mint installation token',
      'create app repo',
      'mint repo token',
    ])
  ) {
    return 'github';
  }

  if (includesOneOf(message, ['neon', 'create neon project'])) {
    return 'neon';
  }

  if (
    includesOneOf(message, ['vercel']) &&
    !includesOneOf(message, ['vercel service is not configured'])
  ) {
    return 'vercel';
  }

  return null;
};

export const isGenericDispatchSetupError = (message?: string): boolean =>
  (message?.toLowerCase() ?? '').includes(GENERIC_DISPATCH_SETUP_ERROR);

export const isBlockingInfraOutageClassified = (
  error: ClassifiedAppBuilderError,
): error is ClassifiedAppBuilderError & {
  infraProvider: BlockingInfraProvider;
} => error.laterReason === 'infra_outage' && error.infraProvider !== undefined;

// True for a classified credits refusal, whichever gate produced it — the
// Studio balance gate or the legacy token quota. The banner is the same either
// way, so the composer routes both to OutOfBuildCreditsBanner.
export const isOutOfCreditsClassified = (
  error: ClassifiedAppBuilderError,
): boolean => error.laterReason === 'credits';

// One marker per backend producer: the legacy quota error
// (quota.ErrAppBuilderQuotaExceeded) and the Studio credit gate
// (studio_credit.ErrInsufficientCredits). The latter's code is
// `studio_credit_insufficient_credits`, not `rate_limit_exceeded`, so its
// message is the only thing that catches it.
const CREDITS_MARKERS = ['app builder credit limit', 'out of build credits'];

const includesOneOf = (value: string, needles: string[]): boolean =>
  needles.some((needle) => value.includes(needle));

const isOutOfCreditsError = (code: string, message: string): boolean =>
  code === 'rate_limit_exceeded' || includesOneOf(message, CREDITS_MARKERS);

const isTurnBusyError = (
  code: string,
  message: string,
  status?: number,
): boolean =>
  status === 409 ||
  code === 'turn_in_progress' ||
  code === 'build_already_started' ||
  includesOneOf(message, [
    'turn is already in progress',
    'turn already in progress',
    'build already in progress',
  ]);

// API and SSE payloads nest errors under `data` or `error`; cap depth so a
// cyclic structure cannot blow the stack.
const readIdentity = (value: unknown, depth = 0): AppBuilderErrorIdentity => {
  if (depth > 3) return {};
  if (typeof value === 'string') return { message: value };
  if (value instanceof AppBuilderRequestError) {
    return {
      code: value.code,
      message: value.message,
      status: value.status,
      source: value.source,
    };
  }
  if (value instanceof Error) {
    const status =
      'status' in value && typeof value.status === 'number'
        ? value.status
        : undefined;
    return status === undefined
      ? { message: value.message }
      : { message: value.message, status };
  }
  if (typeof value !== 'object' || value === null) return {};

  const ownIdentity: AppBuilderErrorIdentity = {};
  if ('code' in value && typeof value.code === 'string') {
    ownIdentity.code = value.code;
  }
  if ('message' in value && typeof value.message === 'string') {
    ownIdentity.message = value.message;
  }
  if ('status' in value && typeof value.status === 'number') {
    ownIdentity.status = value.status;
  }

  const nested =
    'data' in value
      ? readIdentity(value.data, depth + 1)
      : 'error' in value
        ? readIdentity(value.error, depth + 1)
        : {};

  return {
    code: ownIdentity.code ?? nested.code,
    message: ownIdentity.message ?? nested.message,
    status: ownIdentity.status ?? nested.status,
  };
};

export const toAppBuilderErrorIdentity = (
  error: unknown,
  source?: AppBuilderErrorIdentity['source'],
): AppBuilderErrorIdentity => {
  const identity = readIdentity(error);
  return source ? { ...identity, source } : identity;
};

/** Terminal HTTP/code failures when loading a builder by id (missing, deleted, or foreign). */
export const isUnreachableBuilderLoadError = (error: unknown): boolean => {
  const { code, status } = toAppBuilderErrorIdentity(error);
  return (
    (status !== undefined && TERMINAL_STATUSES.has(status)) ||
    (code !== undefined && TERMINAL_CODES.has(code))
  );
};

// PA SSE `error` events and non-OK HTTP bodies arrive as raw strings.
export const parseAppBuilderErrorPayload = (
  data: string,
): AppBuilderErrorIdentity => {
  if (!data.trim()) return {};
  try {
    const parsed: unknown = JSON.parse(data);
    return readIdentity(parsed);
  } catch {
    return { message: data };
  }
};

// Stream callers default to `stream`, but deploy failures only classify as
// terminal when source is `builder` — infer that from the message text.
export const resolveAppBuilderErrorSource = (
  identity: AppBuilderErrorIdentity,
  fallback: NonNullable<AppBuilderErrorIdentity['source']>,
): NonNullable<AppBuilderErrorIdentity['source']> => {
  if (identity.source) return identity.source;
  const message = identity.message?.toLowerCase() ?? '';
  if (isDeploymentStatusUnknownError(message)) {
    return 'builder';
  }
  if (includesOneOf(message, BUILDER_DEPLOY_FAILURE_MARKERS)) {
    return 'builder';
  }
  return fallback;
};

// Quieter copy for persisted in-thread error parts — shorter than banner text
// because transcript markers are historical footnotes, not recovery affordances.
export const getAppBuilderTranscriptError = (
  classified: ClassifiedAppBuilderError,
): string => {
  if (classified.category === 'terminal') {
    return APP_BUILDER_TRANSCRIPT_ERROR_TERMINAL;
  }
  if (classified.laterReason === 'budget_exceeded') {
    return APP_BUILDER_TRANSCRIPT_ERROR_BUDGET_EXCEEDED;
  }
  if (classified.laterReason === 'credits') {
    return APP_BUILDER_TRANSCRIPT_ERROR_CREDITS;
  }
  return APP_BUILDER_TRANSCRIPT_ERROR_DEFAULT;
};

// Bucket 5 — failures that happen before or outside an active turn (HTTP send,
// SSE setup, missing config). In-thread errors and builder deploy failures take
// precedence so those stay on the composer/header retry paths instead.
export const isOutsideBuilderError = (params: {
  streamError?: AppBuilderErrorState | null;
  configError?: string | null;
  hasInThreadError: boolean;
  hasBuilderFailure: boolean;
}): boolean => {
  if (params.hasInThreadError || params.hasBuilderFailure) return false;

  const streamIdentity =
    typeof params.streamError === 'string'
      ? { message: params.streamError, source: 'stream' as const }
      : (params.streamError ?? null);

  if (
    streamIdentity &&
    detectBlockingInfraProviderOutage(streamIdentity) !== null
  ) {
    return false;
  }

  if (streamIdentity?.source === 'request') return true;
  return !!params.configError && !params.streamError;
};

// Picks the composer error identity. When the in-thread copy is the generic
// dispatch fallback but deployErrors names a provider outage, prefer the durable
// builder failure so the blocking outage UI can surface.
export const resolveComposerErrorIdentity = (params: {
  latestMessageError: AppBuilderErrorIdentity | null;
  builderError?: string | null;
  streamError?: AppBuilderErrorState | null;
  configError?: string | null;
}): AppBuilderErrorIdentity | null => {
  const builderIdentity = params.builderError
    ? { message: params.builderError, source: 'builder' as const }
    : null;
  const streamIdentity =
    typeof params.streamError === 'string'
      ? { message: params.streamError, source: 'stream' as const }
      : (params.streamError ?? null);

  if (
    params.latestMessageError &&
    builderIdentity &&
    isGenericDispatchSetupError(params.latestMessageError.message) &&
    detectBlockingInfraProviderOutage(builderIdentity) !== null
  ) {
    return builderIdentity;
  }

  return (
    params.latestMessageError ??
    builderIdentity ??
    streamIdentity ??
    (params.configError
      ? { message: params.configError, source: 'connection' as const }
      : null)
  );
};

// Maps a normalized identity to user-facing copy and recovery hints. Order
// matters: credits and terminal are checked before transient buckets so a
// rate-limit or deploy failure is never mislabeled retryable. Retryable
// categories use warning (yellow) except credits (info/blue); terminal uses
// error (red). Outside-builder copy is not produced here — callers use
// OUTSIDE_BUILDER_ERROR_MESSAGE when isOutsideBuilderError is true.
// The composer banner for a failed send — retryable now (Try again resends the
// same message). Built directly rather than routed through classifyAppBuilderError
// because a send failure carries no identity/code to classify.
export const classifySendFailed = (): ClassifiedAppBuilderError => ({
  category: 'retryable_now',
  message: SEND_FAILED_BANNER_MESSAGE,
  variant: 'warning',
  offerRetry: true,
});

export const classifyAppBuilderError = (
  identity: AppBuilderErrorIdentity,
): ClassifiedAppBuilderError => {
  const code = identity.code?.toLowerCase() ?? '';
  const message = identity.message?.toLowerCase() ?? '';

  // Per-turn credit cap abort — not a workspace-empty state; Try again is useful
  // because a smaller follow-up can succeed under the same balance.
  if (code === BUDGET_EXCEEDED_REASON_CODE) {
    return {
      category: 'retryable_later',
      laterReason: 'budget_exceeded',
      message: BUDGET_EXCEEDED_BANNER_MESSAGE,
      variant: 'warning',
      offerRetry: true,
    };
  }

  // Bucket 3 — routed out of the error banner to OutOfBuildCreditsBanner, which
  // owns the copy and the Upgrade CTA. Try again is never offered: it cannot
  // succeed until the workspace has credits again.
  if (isOutOfCreditsError(code, message)) {
    return {
      category: 'retryable_later',
      laterReason: 'credits',
      message: OUT_OF_CREDITS_CLASSIFIED_MESSAGE,
      variant: 'warning',
      offerRetry: false,
    };
  }

  if (
    identity.source === 'builder' &&
    isDeploymentStatusUnknownError(message)
  ) {
    return {
      category: 'retryable_now',
      message: DEPLOYMENT_STATUS_UNKNOWN_MESSAGE,
      variant: 'warning',
      offerRetry: false,
    };
  }

  const blockingInfraProvider = detectBlockingInfraProviderOutage(identity);
  if (blockingInfraProvider) {
    return {
      category: 'retryable_later',
      laterReason: 'infra_outage',
      infraProvider: blockingInfraProvider,
      message: BLOCKING_INFRA_OUTAGE_CLASSIFIED_MESSAGE,
      variant: 'warning',
    };
  }

  const isTerminalMessage =
    includesOneOf(message, [
      'mint repo token',
      'plan generation failed',
      'could not be parsed into tasks',
    ]) ||
    (identity.source === 'builder' &&
      includesOneOf(message, BUILDER_DEPLOY_FAILURE_MARKERS)) ||
    includesOneOf(message, [
      'not configured',
      'http 401',
      'http 403',
      'http 404',
    ]);

  if (
    TERMINAL_CODES.has(code) ||
    (identity.status !== undefined && TERMINAL_STATUSES.has(identity.status)) ||
    isTerminalMessage
  ) {
    return {
      category: 'terminal',
      message: TERMINAL_ERROR_MESSAGE,
      variant: 'error',
    };
  }

  if (isProviderOutageError(message, identity.status)) {
    return {
      category: 'retryable_later',
      laterReason: 'provider_outage',
      message: PROVIDER_OUTAGE_ERROR_MESSAGE,
      variant: 'warning',
    };
  }

  if (isTurnBusyError(code, message, identity.status)) {
    return {
      category: 'retryable_later',
      laterReason: 'try_later',
      message: TRY_LATER_ERROR_MESSAGE,
      variant: 'warning',
    };
  }

  if (
    (identity.status !== undefined &&
      TRANSIENT_STATUSES.has(identity.status)) ||
    includesOneOf(message, [
      'connection',
      'network',
      'stream',
      'temporary',
      'timed out',
      'timeout',
    ])
  ) {
    return {
      category: 'retryable_now',
      message: DEFAULT_ERROR_MESSAGE,
      variant: 'warning',
    };
  }

  // Unknown failures default to retryable so we never strand progress behind a
  // false terminal state.
  return {
    category: 'retryable_now',
    message: DEFAULT_ERROR_MESSAGE,
    variant: 'warning',
  };
};

// Structured throw type so stream and mutation callers preserve code/status for
// classifyAppBuilderError without ad-hoc Error subclass checks.
export class AppBuilderRequestError extends Error {
  readonly code?: string;
  readonly status?: number;
  readonly source?: AppBuilderErrorIdentity['source'];

  constructor(identity: AppBuilderErrorIdentity) {
    super(identity.message || DEFAULT_ERROR_MESSAGE);
    this.name = 'AppBuilderRequestError';
    this.code = identity.code;
    this.status = identity.status;
    this.source = identity.source;
  }
}
